import { Module } from "vuex";
import type { EmailReceivedPayload } from "@/dtos/EmailWebhook.dto";

export interface Email {
  emailId: string;
  senderEmail: string;
  subject: string | null;
  timestamp: string;
  contactId: string | null;
  accountId: string;
  isRead: boolean;
  metadata?: any;
}

interface EmailState {
  emails: Email[];
  unreadCount: number;
}

/**
 * Helper function to update browser tab title
 */
function updateBrowserTabTitle(unreadCount: number): void {
  if (typeof document !== "undefined") {
    const baseTitle = document.title.split(") ").pop() || "SalesBuckets";
    const cleanTitle = baseTitle.replace(/^\(\d+\)\s*/, "");
    document.title = unreadCount > 0 ? `(${unreadCount}) ${cleanTitle}` : cleanTitle;
  }
}

const emailModule: Module<EmailState, any> = {
  namespaced: true,

  state: {
    emails: [],
    unreadCount: 0,
  },

  mutations: {
    ADD_EMAIL(state, email: Email) {
      // Check if email already exists
      const exists = state.emails.some((e) => e.emailId === email.emailId);
      if (exists) return;

      // Add to beginning of list
      state.emails.unshift(email);

      // Increment unread count if not read
      if (!email.isRead) {
        state.unreadCount += 1;
      }

      // Update browser tab title
      updateBrowserTabTitle(state.unreadCount);
    },

    MARK_EMAIL_AS_READ(state, emailId: string) {
      const email = state.emails.find((e) => e.emailId === emailId);
      if (email && !email.isRead) {
        email.isRead = true;
        state.unreadCount = Math.max(0, state.unreadCount - 1);
        updateBrowserTabTitle(state.unreadCount);
      }
    },

    MARK_ALL_AS_READ(state) {
      state.emails.forEach((email) => {
        email.isRead = true;
      });
      state.unreadCount = 0;
      updateBrowserTabTitle(0);
    },

    SET_EMAILS(state, emails: Email[]) {
      state.emails = emails;
      state.unreadCount = emails.filter((e) => !e.isRead).length;
      updateBrowserTabTitle(state.unreadCount);
    },

    CLEAR_EMAILS(state) {
      state.emails = [];
      state.unreadCount = 0;
      updateBrowserTabTitle(0);
    },

    SET_UNREAD_COUNT(state, count: number) {
      state.unreadCount = Math.max(0, count);
      updateBrowserTabTitle(state.unreadCount);
    },
  },

  actions: {
    addEmail({ commit }, payload: EmailReceivedPayload["payload"]) {
      const email: Email = {
        emailId: payload.emailId,
        senderEmail: payload.senderEmail,
        subject: payload.subject,
        timestamp: payload.eventTimestamp,
        contactId: payload.contactId,
        accountId: payload.accountId,
        isRead: false,
        metadata: payload.metadata,
      };

      commit("ADD_EMAIL", email);
    },

    markAsRead({ commit }, emailId: string) {
      commit("MARK_EMAIL_AS_READ", emailId);
    },

    markAllAsRead({ commit }) {
      commit("MARK_ALL_AS_READ");
    },

    setEmails({ commit }, emails: Email[]) {
      commit("SET_EMAILS", emails);
    },

    clearEmails({ commit }) {
      commit("CLEAR_EMAILS");
    },
  },

  getters: {
    unreadEmails: (state) => state.emails.filter((e) => !e.isRead),
    emailsByContact: (state) => (contactId: string | null) => {
      if (contactId === null) {
        return state.emails.filter((e) => e.contactId === null);
      }
      return state.emails.filter((e) => e.contactId === contactId);
    },
    hasUnread: (state) => state.unreadCount > 0,
    emailsByAccount: (state) => (accountId: string) => {
      return state.emails.filter((e) => e.accountId === accountId);
    },
  },
};

export default emailModule;
